import { useCallback, useMemo, useState } from 'react';

import {
  useGetPolicyPremiumDetails,
  useGetPolicyTravelPremiumDetails
} from '@features/production/policy/policy.slice';
import { capitalizeFirstCharLowerOthers } from '@utils/formatting-utils';
import { getGenericLine, getLineCategory } from '@utils/helper-utils';

import {
  useGetProposalGroupMedicalPremiumDetails,
  useGetProposalPremiumDetails,
  useGetProposalTravelPremiumDetails
} from '../proposal.slice';
import { GetProposalPremiumDetailsVariables } from '../types';

import { GenericLines, LineCategory } from 'src/enums';

export function useGetPremiumDetails(
  lineCode: string,
  type: 'policy' | 'proposal',
  entityID: string,
  defaultSorting: {
    pageNumber: number;
    searchKey: string;
    pageSize: number;
    order: 'asc' | 'desc';
    orderBy: string;
  },
  isCertificate = false
) {
  const isProposal = type === 'proposal';
  const isPolicy = type === 'policy';
  const lineCategory = getLineCategory(lineCode);
  const isGroupMedical =
    getGenericLine(lineCode) === GenericLines.MEDICAL_GROUP;
  const [premiumDetailsKey, setPremiumDetailsKey] = useState<number>(0);
  const [getProposalPremiumData] = useGetProposalPremiumDetails({
    skip: !(isProposal && lineCategory === LineCategory.Medical)
  });

  const [getPolicyPremiumData] = useGetPolicyPremiumDetails({
    skip: !(
      isPolicy &&
      (lineCategory === LineCategory.Medical ||
        (isGroupMedical && isCertificate))
    )
  });

  const [getProposalTravelPremiumData] = useGetProposalTravelPremiumDetails({
    skip: !(isProposal && lineCategory === LineCategory.Travel)
  });

  const [getPolicyTravelPremiumData] = useGetPolicyTravelPremiumDetails({
    skip: !(isPolicy && lineCategory === LineCategory.Travel)
  });

  const [getProposalGroupMedicalPremiumData] =
    useGetProposalGroupMedicalPremiumDetails({
      skip: !(isProposal && isGroupMedical)
    });

  const handlers = useMemo(
    () => ({
      proposalGroupMedical: {
        enabled: isProposal && isGroupMedical,
        fetcher: getProposalGroupMedicalPremiumData,
        buildVariables: (baseVars: any) => ({
          proposalID: entityID,
          ...baseVars
        }),
        extractResponse: (r: any) =>
          r?.data?.production?.getGroupProposalInsuredPolicyQuoteMedical
      },
      proposalMedical: {
        enabled:
          isProposal &&
          lineCategory === LineCategory.Medical &&
          !isGroupMedical,
        fetcher: getProposalPremiumData,
        buildVariables: (
          baseVars: any
        ): GetProposalPremiumDetailsVariables => ({
          proposalID: entityID,
          ...baseVars
        }),
        extractResponse: (r: any) =>
          r?.data?.production
            ?.getProposalInsuredPolicyQuoteMedicalGroupByPolicyPerson
      },

      policyMedical: {
        enabled:
          isPolicy &&
          (lineCategory === LineCategory.Medical ||
            (isGroupMedical && isCertificate)),
        fetcher: getPolicyPremiumData,
        buildVariables: (baseVars: any) => ({
          policyID: entityID,
          ...baseVars
        }),
        extractResponse: (r: any) =>
          r?.data?.production
            ?.getPolicyInsuredPolicyQuoteMedicalGroupByPolicyPerson
      },
      proposalTravel: {
        enabled: isProposal && lineCategory === LineCategory.Travel,
        fetcher: getProposalTravelPremiumData,
        buildVariables: (
          baseVars: any
        ): GetProposalPremiumDetailsVariables => ({
          proposalID: entityID,
          ...baseVars
        }),
        extractResponse: (r: any) =>
          r?.data?.production
            ?.getProposalInsuredPolicyQuoteTravelGroupByPolicyPerson
      },
      policyTravel: {
        enabled: isPolicy && lineCategory === LineCategory.Travel,
        fetcher: getPolicyTravelPremiumData,
        buildVariables: (baseVars: any) => ({
          policyID: entityID,
          ...baseVars
        }),
        extractResponse: (r: any) =>
          r?.data?.production
            ?.getPolicyInsuredPolicyQuoteTravelGroupByPolicyPerson
      }
    }),
    [
      isProposal,
      isPolicy,
      lineCategory,
      entityID,
      isGroupMedical,
      isCertificate,
      getProposalPremiumData,
      getPolicyPremiumData,
      getProposalTravelPremiumData,
      getPolicyTravelPremiumData,
      getProposalGroupMedicalPremiumData
    ]
  );

  const mapItems = (items: any[]) =>
    items?.map((item: any) => ({
      ...item,
      insured:
        isGroupMedical && !isCertificate
          ? item.insured
          : `${item.policyPerson?.person?.code ?? ''} - ${
              item.policyPerson?.person?.fullName ?? ''
            }`,
      relation: capitalizeFirstCharLowerOthers(
        item.policyPerson?.relation ?? ''
      )
    })) ?? [];

  const fetchData = useCallback(
    async (
      pageNumber = defaultSorting.pageNumber,
      keyword = defaultSorting.searchKey,
      pageSize = defaultSorting.pageSize,
      order: 'asc' | 'desc' = defaultSorting.order,
      orderBy: string = defaultSorting.orderBy
    ) => {
      const baseVariables = {
        keyword,
        pagination: { pageNumber, pageSize },
        orderBy: {
          attribute: orderBy || defaultSorting.orderBy,
          descending: order === 'desc'
        }
      };

      const handler = Object.values(handlers).find((h) => h.enabled);

      if (!handler)
        return {
          pagedItems: [],
          pageNumber,
          pageSize,
          totalCount: 0
        };

      const response = await handler.fetcher({
        variables: handler.buildVariables(baseVariables)
      });

      const data = handler.extractResponse(response);
      const rawItems = data?.items ?? [];
      const items = mapItems(rawItems);

      const paging = {
        pageNumber: data?.paging?.pageNumber ?? pageNumber,
        pageSize: data?.paging?.pageSize ?? pageSize,
        totalCount: data?.paging?.totalCount ?? items.length
      };

      return {
        pagedItems: items,
        ...paging
      };
    },
    [handlers, defaultSorting]
  );

  return { fetchData, premiumDetailsKey, setPremiumDetailsKey };
}
